import { Table, Select, DatePicker, Button } from "antd";
import { getSiteUrl } from "config/config";
import usePublications from "hooks/usePublications";
import { getCurriculumPDF } from "services/Usuarios"
import { FaFilePdf } from "react-icons/fa";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { routesNames } from "utils/Constants";
import ExportExcel from "components/excel/ExportExcel";
import dayjs from "dayjs";
import {
    FETCH_LIMIT,
    RANGE_OPTIONS,
    ESPONTANEA_VALUE,
    getRangeBounds,
    fetchInstitucionesMap,
    resolveInstitucion,
    institucionKey,
} from "utils/postulacionesHelpers";

import { BASE_API } from "config/config"
import useNotification from 'hooks/useNotification';

const { RangePicker } = DatePicker;

export default function PostulationsTable() {
    const { isLoading, response, getPostulateds } = usePublications()
    const { contextHolder, openNotification } = useNotification()
    const [rangeType, setRangeType] = useState("hoy");
    const [customRange, setCustomRange] = useState(null);
    const [institucionesMap, setInstitucionesMap] = useState(new Map());
    const [institucionFilter, setInstitucionFilter] = useState([]);
    const [publicacionFilter, setPublicacionFilter] = useState([]);
    const [postulanteFilter, setPostulanteFilter] = useState([]);

    const fetchPdf = async (userId) => {
        const data = await getCurriculumPDF(userId)

        if (data?.uuid) {
            window.open(`${BASE_API}cv/${data.filename}`, "_blank", "noreferrer");
        } else {
            openNotification('El curriculum se encuentra incompleto', 'success', 'Atención!')
        }
    }

    useEffect(() => {
        getPostulateds(null, FETCH_LIMIT)
        fetchInstitucionesMap().then(setInstitucionesMap)
    }, [])

    // Al cambiar el rango de fecha, las opciones de institución/publicación
    // cambian, así que reiniciamos esos filtros para no dejar seleccionados
    // valores que ya no aplican.
    useEffect(() => {
        setInstitucionFilter([]);
        setPublicacionFilter([]);
        setPostulanteFilter([]);
    }, [rangeType, customRange]);

    // Al cambiar la institución filtrada, las opciones de "Publicación" cambian
    // (quedan acotadas a esa institución), así que reiniciamos la publicación
    // seleccionada para no dejar tildado algo que ya no corresponde.
    // Ojo: comparamos por contenido (no por referencia) porque el onChange de la
    // tabla vuelve a llamar a setInstitucionFilter con un array nuevo cada vez que
    // cambia CUALQUIER filtro, aunque el valor de institución no haya cambiado.
    const institucionFilterKey = institucionFilter.slice().sort().join(",");

    useEffect(() => {
        setPublicacionFilter([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institucionFilterKey]);

    const dateFilteredData = useMemo(() => {
        const [from, to] = getRangeBounds(rangeType, customRange);
        if (!from || !to) return [];

        return (response || []).filter((item) => {
            const created = dayjs(item.created_at);
            return !created.isBefore(from) && !created.isAfter(to);
        });
    }, [response, rangeType, customRange]);

    const institucionOptions = useMemo(() => {
        const map = new Map();

        dateFilteredData.forEach((record) => {
            const cuenta = resolveInstitucion(record, institucionesMap);
            const key = institucionKey(record, institucionesMap);
            if (key && cuenta?.nombre && !map.has(key)) {
                map.set(key, { text: cuenta.nombre, value: key });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.text.localeCompare(b.text));
    }, [dateFilteredData, institucionesMap]);

    // Si ya se filtró por institución, las opciones de "Publicación" solo muestran
    // las publicaciones de esa/s institución/es — para poder acotar dentro de un
    // colegio puntual en vez de buscar entre las de toda la plataforma.
    const publicacionOptions = useMemo(() => {
        const map = new Map();
        let hasEspontanea = false;

        const scopedData = institucionFilter.length > 0
            ? dateFilteredData.filter((record) => {
                const key = institucionKey(record, institucionesMap);
                return key && institucionFilter.includes(key);
            })
            : dateFilteredData;

        scopedData.forEach((record) => {
            if (record.publicacion_id != null && record.publicacion?.nombre) {
                map.set(record.publicacion_id, { text: record.publicacion.nombre, value: record.publicacion_id });
            } else if (record.publicacion_id == null) {
                hasEspontanea = true;
            }
        });

        const options = Array.from(map.values()).sort((a, b) => a.text.localeCompare(b.text));
        if (hasEspontanea) options.push({ text: "Espontánea", value: ESPONTANEA_VALUE });
        return options;
    }, [dateFilteredData, institucionFilter, institucionesMap]);

    const postulanteOptions = useMemo(() => {
        const map = new Map();

        dateFilteredData.forEach((record) => {
            const nombre = `${record.postulante?.nombre || ""} ${record.postulante?.apellido || ""}`.trim();
            if (record?.user_id != null && nombre && !map.has(record.user_id)) {
                map.set(record.user_id, { text: nombre, value: record.user_id });
            }
        });

        return Array.from(map.values()).sort((a, b) => a.text.localeCompare(b.text));
    }, [dateFilteredData]);

    const filteredData = useMemo(() => {
        return dateFilteredData.filter((record) => {
            if (institucionFilter.length > 0) {
                const key = institucionKey(record, institucionesMap);
                if (!key || !institucionFilter.includes(key)) return false;
            }

            if (publicacionFilter.length > 0) {
                const pubValue = record.publicacion_id != null ? record.publicacion_id : ESPONTANEA_VALUE;
                if (!publicacionFilter.includes(pubValue)) return false;
            }

            if (postulanteFilter.length > 0 && !postulanteFilter.includes(record?.user_id)) {
                return false;
            }

            return true;
        });
    }, [dateFilteredData, institucionFilter, publicacionFilter, postulanteFilter, institucionesMap]);

    // Chequeo rápido para validar qué tan confiable es el campo "visto" antes de
    // construir algo sobre él (ej. una vista de "postulaciones sin revisar").
    // Se calcula sobre todo el rango de fecha, sin importar los demás filtros.
    const vistoStats = useMemo(() => {
        const total = dateFilteredData.length;
        const vistas = dateFilteredData.filter((r) => r.visto === 1 || r.visto === true).length;
        return {
            total,
            vistas,
            noVistas: total - vistas,
            porcentaje: total > 0 ? Math.round((vistas / total) * 1000) / 10 : 0,
        };
    }, [dateFilteredData]);

    const dataExcel = useMemo(
        () =>
            filteredData.map((item) => {
                const cuenta = resolveInstitucion(item, institucionesMap);

                return {
                    Postulante: `${item.postulante?.nombre || ""} ${item.postulante?.apellido || ""}`.trim(),
                    Email: item.postulante?.email || item.user?.email || "",
                    Institución: cuenta?.nombre || "",
                    Publicación: item?.publicacion_id != null ? item.publicacion?.nombre : "Espontánea",
                    Fecha: dayjs(item.created_at).format("DD/MM/YYYY HH:mm"),
                };
            }),
        [filteredData, institucionesMap]
    );

    const columns = [
        {
            title: 'Postulante',
            dataIndex: 'postulante',
            key: 'postulante',
            filters: postulanteOptions,
            filteredValue: postulanteFilter,
            filterSearch: true,
            render: (text, record) => <Link to={`${routesNames.USER}/${record?.user_id}`} className="text-blue-700">{record.postulante?.nombre} {record.postulante?.apellido}</Link>,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text, record) => record.postulante?.email || record.user?.email || "—",
        },
        {
            title: 'Institución',
            dataIndex: 'institucion',
            key: 'institucion',
            filters: institucionOptions,
            filteredValue: institucionFilter,
            filterSearch: true,
            render: (text, record) => {
                const cuenta = resolveInstitucion(record, institucionesMap);
                if (!cuenta) return "—";
                if (!cuenta.user_id) return cuenta.nombre || "—";

                return (
                    <Link to={`${routesNames.USER}/${cuenta.user_id}`} className="text-blue-700">
                        {cuenta.nombre}
                    </Link>
                );
            },
        },
        {
            title: 'Publicación',
            dataIndex: 'publicacion',
            key: 'publicacion',
            filters: publicacionOptions,
            filteredValue: publicacionFilter,
            filterSearch: true,
            render: (text, record) => (
                record?.publicacion_id != null ? (
                    <Link to={`${routesNames.PUBLICATION}/${record.publicacion?.id}`}>{record.publicacion?.nombre}</Link>
                ) : (
                    <span className="text-gray-600 dark:text-gray-400">Espontánea</span>
                )
            ),
        },
        {
            title: 'Fecha',
            dataIndex: 'created_at',
            render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm"),
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
            defaultSortOrder: "descend",
        },
        {
            title: 'CV',
            dataIndex: 'cv',
            key: 'cv',
            render: (text, record) => {
                return <div className="flex items-center gap-1">
                    <a target='_blank' rel='noopener noreferrer' href={`${getSiteUrl()}cv/${record.postulante?.uuid}/${record.user_id}`} className='bg-main text-xs text-white px-2 rounded-md py-1'>Ver curriculum</a>

                    <a onClick={() => fetchPdf(record?.user_id)} className='bg-red-500 text-xs text-white px-2 rounded-md py-1 flex items-center'><FaFilePdf className='text-white' /> Descargar</a>
                </div>

            }
        },
    ];

    return (
        <div className="flex flex-col gap-3 w-full">
            {contextHolder}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={rangeType}
                        onChange={(value) => setRangeType(value)}
                        options={RANGE_OPTIONS}
                        className="w-[180px]"
                    />

                    {rangeType === "personalizado" && (
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={customRange}
                            onChange={(value) => setCustomRange(value)}
                        />
                    )}

                    <Button onClick={() => getPostulateds(null, FETCH_LIMIT)} loading={isLoading}>
                        Actualizar
                    </Button>
                </div>

                {filteredData.length > 0 && (
                    <ExportExcel data={dataExcel} fileName={"Postulaciones_" + new Date().toLocaleDateString()} title="Descargar Excel" />
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold">
                    {filteredData.length} postulaciones en el rango seleccionado
                </span>

                {vistoStats.total > 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        (chequeo del campo "visto": {vistoStats.vistas} vistas / {vistoStats.noVistas} no vistas de {vistoStats.total} en todo el rango — {vistoStats.porcentaje}%)
                    </span>
                )}
            </div>

            <Table
                className="w-full"
                loading={isLoading}
                columns={columns}
                dataSource={filteredData}
                rowKey={(record) => record?.id}
                pagination={{
                    pageSize: 15,
                    showSizeChanger: false,
                }}
                size="small"
                locale={{
                    emptyText: "No hay postulaciones en el rango seleccionado"
                }}
                onChange={(pagination, tableFilters, sorter, extra) => {
                    if (extra.action === "filter") {
                        setInstitucionFilter(tableFilters.institucion || []);
                        setPublicacionFilter(tableFilters.publicacion || []);
                        setPostulanteFilter(tableFilters.postulante || []);
                    }
                }}
            />
        </div>
    )
}
